import React, { useContext, useEffect } from "react";
import MobileFrameHomeView from "../../views/mobile-frame/home/MobileFrameHomeView";
import { ImageType } from "../../images/svg";
import { AuthContext } from "../../context/auth/AuthContext";
import Error from "../../components/Error";
import Loading from "../../components/Loading";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getMobileFrameData,
  getMobileFrameDataSuccess,
  mobileFrameResetState,
} from "../../store/mobile-frame-home-page.store";
import {
  getEklokDataSelector,
  getSoundBlockLevelSelector,
  resetManualCommandListState,
} from "../../store/data/common-data.store";
import { useHistory } from "react-router";
import PullToRefresh from "react-simple-pull-to-refresh";
import { Box, makeStyles } from "@material-ui/core";
import { eventStatusListPageResetState } from "../../store/event-status-list-page.store";
import { commandListPageResetState } from "../../store/command-list-page.store";
import { commandDetailPageResetState } from "../../store/command-detail-page.store";
import { blocksSoundsResetState } from "../../store/blocks-sounds-page.store";
import { assistanceResetState } from "../../store/assistance-page.store";
import {
  connectionLineStatusResetState,
  initConnectionLineStatus,
} from "../../store/eklok-line-status.store";
import { useMediaQuery } from "../../hooks/mediaQuery";
import { UseMe } from "../../api/users/Me.api";

const useStyles = makeStyles((theme) => ({
  container: {
    justifyContent: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
}));

export const useMobileFrame = () => {
  const dispatch = useDispatch();

  const goToMobileFrame = (history, eklokId) => {
    dispatch(mobileFrameResetState());
    dispatch(eventStatusListPageResetState());
    dispatch(commandListPageResetState());
    dispatch(commandDetailPageResetState());
    dispatch(blocksSoundsResetState());
    dispatch(assistanceResetState());
    dispatch(resetManualCommandListState());
    history.push("/app/mobile/frame/" + eklokId);
  };

  return goToMobileFrame;
};

const MobileFrameHomeContainer = ({ eklok }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const isMobile = useMediaQuery();
  const { eklok: eklokSelected } = useSelector((state) => {
    return state.eklokLineStatus;
  });

  const { token } = useContext(AuthContext);
  const { content: user } = UseMe(token);

  const { dataFetched, error, loading } = useSelector((state) => {
    return state.mobileFrame;
  });
  const eklokData = useSelector(getEklokDataSelector);
  const soundBlockLevel = useSelector(getSoundBlockLevelSelector);

  useEffect(() => {
    const unblockNavigation = history.block((history) => {
      if (
        (history.pathname === "/app/ekloks" ||
          history.pathname === "/app/plants" ||
          (user.groups[0].name === "Installer" &&
            history.pathname === "/app/dashboard") ||
          (user.groups[0].name === "Customer" &&
            history.pathname === "/app/dashboard")) &&
        isMobile &&
        eklokSelected
      ) {
        dispatch(connectionLineStatusResetState());
        dispatch(
          initConnectionLineStatus({
            token: token,
            body: { eklok: eklokSelected, line_status: false },
          }),
        );
      }
    });

    return () => {
      unblockNavigation();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, isMobile, eklokSelected]);

  let isPlayer = false;
  let last_mqtt_json = eklokData?.last_mqtt_json;
  if (last_mqtt_json) {
    try {
      let modelType = JSON.parse(last_mqtt_json.replace(/'/g, '"'))?.MODEL_TYPE;
      isPlayer = modelType === "Player" || modelType === "EKLOK-PLAYER" || modelType === "EKLOK-V2";
    } catch (e) {
      isPlayer = false;
    }
  }


  const sections = {
    blocksSounds: {
      title: t("mobile_frame.home.blocks_sounds"),
      image: ImageType.OnlySoundsAndHours,
      onClick: (eklok) => {
        history.push("/app/blocks/sounds/" + eklok);
      },
    },
    favorites: {
      title: t("mobile_frame.home.favorites"),
      image: ImageType.Favorites,
      onClick: (eklok) => {
        history.push("/app/command/list/favorites/" + eklok);
      },
    },
    routine: {
      title: t("mobile_frame.home.routines"),
      image: ImageType.Routine,
      onClick: (eklok) => {
        history.push("/app/command/list/routines/" + eklok);
      },
    },
    melodies: {
      title: t("mobile_frame.home.melodies"),
      image: ImageType.Melodies,
      onClick: (eklok) => {
        history.push("/app/command/list/melodies/" + eklok);
      },
    },
    music: {
      title: t("mobile_frame.home.musics"),
      image: ImageType.Music,
      onClick: (eklok) => {
        history.push("/app/command/list/musics/" + eklok);
      },
    },
    musicsSynth: {
      title: t("mobile_frame.home.musicsSynth"),
      image: isPlayer ? ImageType.MusicsSynth : ImageType.MusicsSynth_disabled,
      onClick: (eklok) => {
        history.push("/app/command/list/musicsSynth/" + eklok);
      },
      disabled: !isPlayer,
    },
    // dialCorrection: {
    //   title: t('mobile_frame.home.dialCorrection'),
    //   image: ImageType.DialCorrection,
    //   disabled: true,
    // },
    assistance: {
      title: t("mobile_frame.home.assistance"),
      image: ImageType.Assistance,
      onClick: (eklok) => {
        history.push("/app/assistance/" + eklok);
      },
    },
    calendarEvent: {
      title: t("mobile_frame.home.calendar_event"),
      image: ImageType.CalendarEvent,
      onClick: (eklok) => {
        history.push("/app/calendar/event/" + eklok);
      },
    },
  };

  const onRefresh = () =>
    new Promise((resolve, reject) => {
      dispatch(
        getMobileFrameData({
          token: token,
          eklok: eklok,
        }),
      );
      resolve();
    });

  useEffect(() => {
    if (!eklokData || eklokData.id !== eklok || soundBlockLevel === null) {
      dispatch(
        getMobileFrameData({
          token: token,
          eklok: eklok,
        }),
      );
    } else {
      dispatch(getMobileFrameDataSuccess());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PullToRefresh onRefresh={onRefresh} pullingContent={false}>
      {loading ? <Loading /> : null}
      <Box className={classes.container}>
        {error ? <Error message={{ message: error.message }} /> : null}
        {dataFetched ? (
          <MobileFrameHomeView sections={sections} eklokData={eklokData} />
        ) : null}
      </Box>
    </PullToRefresh>
  );
};

export default MobileFrameHomeContainer;
